// ============================================================
// Prepared answer for the `stats-ask` AI task (#71): picks one of a
// few topics by keywords and answers from the same facts the AI
// gets. Plain JS without aliases: the Worker imports it too.
// Demo answers are Czech or English; the AI answers in any language.
// ============================================================

const TOPICS = [
  ['carriers', /dopravc|carrier|przewo|spedit|nejhorší|worst|problem/i],
  ['savings', /ušetř|úspor|save|saving|oszczęd|spar|hodin|hours|godzin|stunden|nákl|cost|koszt|kosten/i],
  ['informed', /informov|13:00|informed|poinform|informiert|vědět|know/i],
  ['parking', /parkov|parking|jeseník|parkplatz/i],
];

const fmt = (n, lang) => Number(n).toLocaleString(lang === 'cs' ? 'cs-CZ' : 'en-GB', { maximumFractionDigits: 1 });

const ANSWERS = {
  carriers: (f, l) => {
    const worst = [...f.carriers30].sort((a, b) => b.noShowPct - a.noShowPct);
    const w = worst[0];
    return {
      answer: l === 'cs'
        ? `Nejvíc nepřijetí bez avíza má ${w.name}: ${fmt(w.noShowPct, l)} % nakládek za posledních 30 dní, odpovídá za ${w.replyMedianMin ?? '—'} min (medián).`
        : `${w.name} has the most no-shows without notice: ${fmt(w.noShowPct, l)} % of loadings in the last 30 days, replying in ${w.replyMedianMin ?? '—'} min (median).`,
      figures: worst.slice(0, 3).map(c => ({ label: c.name, value: `${fmt(c.noShowPct, l)} %` })),
      chart: { type: 'bar', title: l === 'cs' ? 'Nepřijetí bez avíza (%)' : 'No-shows without notice (%)', points: worst.map(c => ({ label: c.name.split(' ')[0], value: c.noShowPct })) },
      source: l === 'cs' ? 'Statistiky → Dopravci, posledních 30 dní' : 'Statistics → Carriers, last 30 days',
    };
  },
  savings: (f, l) => ({
    answer: l === 'cs'
      ? `Automatická urgence vyřešila ${f.followUps30.autoResolved} z ${f.followUps30.total} urgencí sama. Plánovači to ušetří zhruba ${fmt(f.followUps30.savedPlannerHoursPerMonth, l)} h měsíčně při nákladech na zprávy ${f.followUps30.channelCostCzkPerMonth} Kč.`
      : `The automatic follow-up resolved ${f.followUps30.autoResolved} of ${f.followUps30.total} follow-ups on its own, saving the planner about ${fmt(f.followUps30.savedPlannerHoursPerMonth, l)} h a month at ${f.followUps30.channelCostCzkPerMonth} CZK for messages.`,
    figures: [
      { label: l === 'cs' ? 'Ušetřeno' : 'Saved', value: `${fmt(f.followUps30.savedPlannerHoursPerMonth, l)} h` },
      { label: l === 'cs' ? 'Vyřešeno samo' : 'Resolved automatically', value: `${fmt((f.followUps30.autoResolved / f.followUps30.total) * 100, l)} %` },
    ],
    chart: null,
    source: l === 'cs' ? 'Statistiky → Urgence' : 'Statistics → Follow-ups',
  }),
  informed: (f, l) => ({
    answer: l === 'cs'
      ? `Ve 13:00 teď víme o ${fmt(f.kpis30.informedAt13Pct.now, l)} % nakládek, před pilotem to bylo ${fmt(f.kpis30.informedAt13Pct.beforePilot, l)} %.`
      : `At 13:00 we now know about ${fmt(f.kpis30.informedAt13Pct.now, l)} % of loadings, against ${fmt(f.kpis30.informedAt13Pct.beforePilot, l)} % before the pilot.`,
    figures: [{ label: l === 'cs' ? 'Teď' : 'Now', value: `${fmt(f.kpis30.informedAt13Pct.now, l)} %` }, { label: l === 'cs' ? 'Před pilotem' : 'Before pilot', value: `${fmt(f.kpis30.informedAt13Pct.beforePilot, l)} %` }],
    chart: { type: 'line', title: l === 'cs' ? 'Informováno ve 13:00 po týdnech (%)' : 'Informed at 13:00 per week (%)', points: f.weekly.map(w => ({ label: w.week.slice(5), value: w.informedPct })) },
    source: l === 'cs' ? 'Statistiky → Přehled KPI' : 'Statistics → KPI overview',
  }),
  parking: (f, l) => ({
    answer: l === 'cs'
      ? `Na parkovišti Jeseník teď stojí průměrně ${fmt(f.kpis30.parkingTrucksPerNight.now, l)} aut za noc, před pilotem ${fmt(f.kpis30.parkingTrucksPerNight.beforePilot, l)}.`
      : `The Jeseník parking now holds ${fmt(f.kpis30.parkingTrucksPerNight.now, l)} trucks a night on average, against ${fmt(f.kpis30.parkingTrucksPerNight.beforePilot, l)} before the pilot.`,
    figures: [{ label: l === 'cs' ? 'Teď' : 'Now', value: fmt(f.kpis30.parkingTrucksPerNight.now, l) }, { label: l === 'cs' ? 'Před pilotem' : 'Before pilot', value: fmt(f.kpis30.parkingTrucksPerNight.beforePilot, l) }],
    chart: { type: 'bar', title: l === 'cs' ? 'Auta na parkovišti za noc' : 'Trucks at the parking per night', points: f.weekly.map(w => ({ label: w.week.slice(5), value: w.parkingTrucks })) },
    source: l === 'cs' ? 'Statistiky → Přehled KPI' : 'Statistics → KPI overview',
  }),
  noShows: (f, l) => ({
    answer: l === 'cs'
      ? `Za posledních 30 dní nepřijede bez avíza průměrně ${fmt(f.kpis30.noShowsPerDay.now, l)} aut denně, před pilotem to bylo ${fmt(f.kpis30.noShowsPerDay.beforePilot, l)}.`
      : `In the last 30 days ${fmt(f.kpis30.noShowsPerDay.now, l)} trucks a day did not come without notice on average, against ${fmt(f.kpis30.noShowsPerDay.beforePilot, l)} before the pilot.`,
    figures: [{ label: l === 'cs' ? 'Teď / den' : 'Now / day', value: fmt(f.kpis30.noShowsPerDay.now, l) }, { label: l === 'cs' ? 'Před pilotem' : 'Before pilot', value: fmt(f.kpis30.noShowsPerDay.beforePilot, l) }],
    chart: { type: 'bar', title: l === 'cs' ? 'Nepřijetí bez avíza po týdnech' : 'No-shows without notice per week', points: f.weekly.map(w => ({ label: w.week.slice(5), value: w.noShows })) },
    source: l === 'cs' ? 'Statistiky → Přehled KPI' : 'Statistics → KPI overview',
  }),
};

export function statsAskDemo({ question, lang, facts }) {
  const topic = TOPICS.find(([, re]) => re.test(question))?.[0] || 'noShows';
  return ANSWERS[topic](facts, lang === 'cs' ? 'cs' : 'en');
}
